import { Monitor, Moon, Sun } from "lucide-react";
import { THEMES, usePreferences, type Theme } from "../lib/preferences.js";
import { cn } from "../lib/cn.js";

const LABEL: Record<Theme, string> = {
  auto: "Match my device",
  light: "Light",
  dark: "Dark",
};

const ICON: Record<Theme, typeof Sun> = {
  auto: Monitor,
  light: Sun,
  dark: Moon,
};

/**
 * Three states, not two.
 *
 * The design persisted a chosen theme and offered only light and dark. This
 * app followed the operating system before slice 5B, so shipping a two-way
 * toggle would have silently ended that for every existing reader the first
 * time they touched it, with no way back. `auto` is the default and keeps
 * following the device for as long as the tab is open.
 *
 * A radio group rather than a cycling button, because a button that cycles
 * through three states announces only its current one and gives no way to know
 * what the next press will do.
 */
export function ThemeToggle() {
  const { theme, setTheme } = usePreferences();

  return (
    <fieldset className="flex shrink-0 items-center gap-0.5 rounded-full border border-line p-0.5">
      <legend className="sr-only">Theme</legend>
      {THEMES.map((option) => {
        const Icon = ICON[option];
        const selected = theme === option;
        return (
          <label
            key={option}
            className={cn(
              "cursor-pointer rounded-full p-1.5 transition-colors",
              // Solid, not the translucent accent: composited over the page
              // that blend puts accent text at 4.2:1, which axe fails. Same
              // measured pair the filled controls use.
              selected ? "bg-btn text-btn-ink" : "text-ink-muted hover:text-ink",
            )}
          >
            <input
              type="radio"
              name="theme"
              value={option}
              checked={selected}
              onChange={() => setTheme(option)}
              className="sr-only"
            />
            <span className="sr-only">{LABEL[option]}</span>
            <Icon size={16} aria-hidden="true" />
          </label>
        );
      })}
    </fieldset>
  );
}
